import { useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { About } from "@/components/sections/About";
import { Footer } from "@/components/sections/Footer";
import { Hero } from "@/components/sections/Hero";
import { InquiryStats } from "@/components/sections/InquiryStats";
import { Portfolio } from "@/components/sections/Portfolio";
import { Quote } from "@/components/sections/Quote";
import { AdminPage } from "@/pages/AdminPage";
import { InquiryBoardPage } from "@/pages/InquiryBoardPage";
import { InquiryPage } from "@/pages/InquiryPage";
import { gsap, MOTION_OK, useGSAP } from "@/lib/gsap";
import { seoFor, useSeo } from "@/lib/seo";
import { track, type VisitStage } from "@/lib/track";
import dchainConnectMark from "@/assets/brand/dchain-connect-mark.svg";

// Plain anchors (not <Link>) with an absolute "/#…" href: on "/" it's a same-document
// hash navigation (native smooth scroll, no reload); from any other route it does a
// full navigation to "/" and the browser jumps to the hash on load.
const NAV_LINKS = [
  { href: "/#about", label: "소개" },
  { href: "/#portfolio", label: "포트폴리오" },
  { href: "/#quote", label: "견적" },
  { href: "/inquiry", label: "문의" },
];
const MOBILE_NAV_LINKS = NAV_LINKS.slice(2);
const SECTION_IDS = ["about", "portfolio", "quote"];
const SECTION_STAGE: Record<string, VisitStage> = {
  about: "viewed_about",
  portfolio: "viewed_portfolio",
  quote: "viewed_quote",
};

/**
 * 뷰포트 상단(헤더 아래 ~20% 지점)에 걸친 섹션을 현재 섹션으로 봐요.
 * 스크롤 위치 계산 대신 얇은 관찰 띠를 두면, 섹션 높이가 제각각이어도 한 번에 하나만 잡혀요.
 * 히어로에 있을 때는 걸리는 섹션이 없어 아무것도 활성화되지 않아요.
 */
function useActiveSection(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const sections = ids.map((id) => document.getElementById(id)).filter((el) => el !== null);
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const hit = entries.find((entry) => entry.isIntersecting);
        if (hit) setActive(hit.target.id);
      },
      // 위: 헤더 높이(64px)만큼 내려서 시작, 아래: 화면 80%를 잘라내 상단 띠만 남겨요.
      // rootMargin 은 px·% 만 받아서 rem 을 쓸 수 없어요.
      { rootMargin: "-64px 0px -80% 0px" },
    );
    for (const section of sections) observer.observe(section);
    return () => observer.disconnect();
  }, [ids]);

  return active;
}

/**
 * 활성 링크 밑줄은 링크마다 하나씩 두는 대신 목록에 **하나만** 두고 옮겨요 —
 * 그래야 항목 사이를 미끄러지듯 이동하고(사라졌다 다시 나타나는 게 아니라), 마크업도 한 개예요.
 * 걸린 섹션이 없는 히어로 구간에서는 투명해져요.
 */
function NavList({
  links,
  isActive,
  className,
}: {
  links: { href: string; label: string }[];
  isActive: (href: string) => boolean;
  className: string;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const barRef = useRef<HTMLSpanElement>(null);
  const active = links.find((link) => isActive(link.href))?.href ?? null;

  useGSAP(
    () => {
      const bar = barRef.current;
      const list = listRef.current;
      if (!bar || !list) return;

      const place = (duration: number) => {
        const target = list.querySelector<HTMLElement>('[aria-current="true"]');
        // 목록이 breakpoint 로 감춰져 있으면 폭이 0 이라 잴 게 없어요.
        if (!target || target.offsetWidth === 0) {
          gsap.to(bar, { opacity: 0, duration, overwrite: true });
          return;
        }
        gsap.to(bar, {
          opacity: 1,
          x: target.offsetLeft,
          width: target.offsetWidth,
          duration,
          ease: "power2.out",
          overwrite: true,
        });
      };

      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => place(0.28));
      mm.add("(prefers-reduced-motion: reduce)", () => place(0));

      // 폰트 로딩·리사이즈로 링크 폭이 바뀌면 밑줄이 어긋나요.
      const observer = new ResizeObserver(() => place(0));
      observer.observe(list);
      return () => observer.disconnect();
    },
    { dependencies: [active], scope: listRef },
  );

  return (
    <ul ref={listRef} className={`relative ${className}`}>
      {links.map((link) => {
        const current = isActive(link.href);
        return (
          <li key={link.href}>
            <a
              href={link.href}
              aria-current={current ? "true" : undefined}
              className={`wds-label-1 transition-colors duration-200 ease-standard ${
                current ? "font-semibold text-primary" : "text-label-neutral hover:text-primary"
              }`}
            >
              {link.label}
            </a>
          </li>
        );
      })}
      <span
        ref={barRef}
        aria-hidden="true"
        className="-bottom-1.5 pointer-events-none absolute left-0 h-0.5 w-0 rounded-full bg-primary opacity-0"
      />
    </ul>
  );
}

/** 헤더 아래에 붙는 읽은 분량 표시. 문서 전체 스크롤에 매여 있어요. */
function ScrollProgress() {
  const ref = useRef<HTMLSpanElement>(null);

  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add(MOTION_OK, () => {
      gsap.fromTo(
        ref.current,
        { scaleX: 0 },
        {
          scaleX: 1,
          ease: "none",
          scrollTrigger: { trigger: document.documentElement, start: "top top", end: "bottom bottom", scrub: 0.2 },
        },
      );
    });
  });

  return (
    <span
      ref={ref}
      aria-hidden="true"
      className="absolute inset-x-0 bottom-0 h-0.5 origin-left scale-x-0 bg-primary"
    />
  );
}

function HomePage() {
  const activeSection = useActiveSection(SECTION_IDS);
  const isActive = (href: string) => href === `/#${activeSection}`;

  useEffect(() => {
    track("landed");
  }, []);

  useEffect(() => {
    const stage = activeSection ? SECTION_STAGE[activeSection] : undefined;
    if (stage) track(stage);
  }, [activeSection]);

  return (
    <>
      <header className="sticky top-0 z-50 border-line-alternative border-b bg-background-elevated">
        <nav className="mx-auto flex h-16 max-w-layout items-center justify-between px-6">
          <a href="/#top" className="flex shrink-0 items-center gap-2" aria-label="디체인커넥트 홈">
            <img src={dchainConnectMark} alt="" width="34" height="28" className="h-7 w-auto" />
            <span className="wds-headline-1 text-label-strong">디체인커넥트</span>
          </a>
          <NavList links={MOBILE_NAV_LINKS} isActive={isActive} className="flex items-center gap-4 sm:hidden" />
          <NavList links={NAV_LINKS} isActive={isActive} className="hidden items-center gap-6 sm:flex" />
        </nav>
        <ScrollProgress />
      </header>

      <main id="top" className="bg-background-normal">
        <Hero />
        <About />
        <Portfolio />
        <InquiryStats />
        <Quote />
      </main>

      <Footer />
    </>
  );
}

export default function App() {
  // 라우트마다 title·canonical 을 바꿔요 — index.html 의 태그는 SPA 라 그대로 남거든요.
  useSeo(seoFor(useLocation().pathname));

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/inquiry" element={<InquiryPage />} />
      <Route path="/inquiries" element={<InquiryBoardPage />} />
      <Route path="/admin" element={<AdminPage />} />
    </Routes>
  );
}
